import styled, { css, keyframes } from "styled-components";
import { ANIMATION, QUERIES, SPACING } from "../../utils/constants";
import { ButtonSize, LinkTone, StyleProps } from "./types";

const getMinHeight = (size?: ButtonSize) => {
  if (size === "large") return "48px";
  if (size === "small") return "24px";
  return "32px";
};

const getPadding = (size?: ButtonSize) => {
  if (size === "large") return "0.75rem 1.25rem";
  if (size === "small") return "0.375rem 0.75rem";
  return "0.5rem 1rem";
};

const getRadius = (size?: ButtonSize) => {
  if (size === "large") return "0.75rem";
  return "0.5rem";
};

const getFontSize = (size?: ButtonSize) => {
  if (size === "large") return "var(--text-md)";
  if (size === "small") return "var(--text-sm)";
  return "var(--text-base)";
};

const getFontWeight = (size?: ButtonSize) => {
  if (size === "large") return 600;
  return 500;
};

/** Filled surface: solid background, white text, darker background on hover. */
const solid = (background: string, hoverBackground: string) => css`
  background: ${background};
  color: var(--color-white);
  border-color: ${background};

  &:hover:not(:disabled) {
    background: ${hoverBackground};
    border-color: ${hoverBackground};
  }
`;

/**
 * Outline surface: transparent background with an intent-colored border + text,
 * filling solid with that same intent color (white text) on hover.
 * Assumes the intent color is dark enough for white hover text (true for all
 * current intents: error, warning, ternary).
 */
const outline = (color: string) => css`
  background: transparent;
  color: ${color};
  border-color: ${color};

  &:hover:not(:disabled) {
    background: ${color};
    border-color: ${color};
    color: var(--color-white);
  }
`;

export const BaseButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${SPACING.sm}px;
  cursor: pointer;
  user-select: none;
  font-family: "Crimson Pro", sans-serif;

  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: var(--shadow-focus);
  }
  &:disabled,
  &[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.5;
  }
  & > svg {
    flex: none;
  }
`;

export const StyledButton = styled(BaseButton)<StyleProps>`
  padding: ${(props) => getPadding(props.size)};
  border-radius: ${(props) => getRadius(props.size)};
  font-size: ${(props) => getFontSize(props.size)};
  font-weight: ${(props) => getFontWeight(props.size)};
  min-height: ${(props) => getMinHeight(props.size)};
  width: ${(props) => (props.$isFullWidth ? "100%" : "auto")};
  justify-content: center;
  border: 1px solid transparent;
  white-space: nowrap;
  transition: ${ANIMATION.transitions.all};

  &[aria-busy="true"] {
    cursor: wait;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: var(--shadow-hover);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
    transition: transform ${ANIMATION.duration.instant}
      ${ANIMATION.easing.easeOut};
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover:not(:disabled),
    &:active:not(:disabled) {
      transform: none;
    }
  }
`;

/* ---- Solid surfaces ---- */

export const PrimaryButton = styled(StyledButton)`
  ${solid("var(--color-primary)", "var(--color-primary-hover)")}
  color: var(--color-on-primary);
`;

export const SecondaryButton = styled(StyledButton)`
  ${solid("var(--color-secondary)", "var(--color-secondary-hover)")}
`;

export const WarningButton = styled(StyledButton)`
  ${solid("var(--color-warning-2)", "var(--color-warning)")}

  &:disabled {
    background: var(--color-warning-4);
    color: var(--color-gray-500);
    opacity: 1;
  }
`;

export const DangerButton = styled(StyledButton)`
  ${solid("var(--color-error)", "var(--color-error-hover)")}
`;

/* ---- Outline surfaces ---- */

export const CancelButton = styled(StyledButton)`
  background: var(--color-button-cancel-background);
  border-color: var(--color-button-cancel-border);
  color: var(--color-button-cancel-color);

  &:hover:not(:disabled) {
    background: var(--color-button-cancel-hover-background);
    border-color: var(--color-button-cancel-hover-border);
  }
  &:active:not(:disabled) {
    background: var(--color-button-cancel-active-background);
    border-color: var(--color-button-cancel-active-border);
  }
`;

export const RevertButton = styled(StyledButton)`
  background: var(--color-white);
  color: var(--color-black);
  border-color: var(--color-gray-border);

  &:hover:not(:disabled) {
    background: var(--color-gray-900);
  }
  &:disabled {
    background: var(--color-gray-500);
    border-color: var(--color-gray-300);
  }
`;

export const DangerOutlineButton = styled(StyledButton)`
  ${outline("var(--color-error)")}

  @media not all and ${QUERIES.tabletAndUp} {
    width: 100%;
  }
`;

/* ---- Text surfaces ---- */

/**
 * Text/link button: no background or border, muted underlined label that
 * darkens on hover. Used for low-emphasis actions like clearing filters.
 */
export const ClearButton = styled(StyledButton)`
  background: transparent;
  border-color: transparent;
  color: var(--color-text-secondary);
  padding-left: ${SPACING.xs}px;
  padding-right: ${SPACING.xs}px;

  & > span {
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &:hover:not(:disabled) {
    color: var(--color-text-default);
    transform: none;
    box-shadow: none;
  }
  &:active:not(:disabled) {
    transform: none;
  }
`;

// Neutral tones hover to primary; danger stays red so a destructive link never reads as primary.
const LINK_TONE: Record<LinkTone, { rest: string; hover: string }> = {
  primary: {
    rest: "var(--color-primary)",
    hover: "var(--color-primary-hover)",
  },
  secondary: {
    rest: "var(--color-text-secondary)",
    hover: "var(--color-primary)",
  },
  muted: { rest: "var(--color-text-muted)", hover: "var(--color-primary)" },
  danger: { rest: "var(--color-error)", hover: "var(--color-error-hover)" },
};

/** Inline text link: tone-colored label that underlines on hover, or at rest with `$underline`. */
export const LinkButton = styled(StyledButton)<{
  $tone: LinkTone;
  $underline?: boolean;
}>`
  align-self: center;
  background: none;
  border: none;
  min-height: 0;
  padding: ${SPACING.xs}px;
  font-weight: 600;
  color: ${({ $tone }) => LINK_TONE[$tone].rest};
  text-decoration: ${({ $underline }) => ($underline ? "underline" : "none")};
  text-underline-offset: 3px;

  &:hover:not(:disabled) {
    text-decoration: underline;
    color: ${({ $tone }) => LINK_TONE[$tone].hover};
    transform: none;
    box-shadow: none;
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const getSpinnerSize = (size?: ButtonSize): string => {
  if (size === "small") return "14px";
  if (size === "large") return "20px";
  return "16px";
};

export const Spinner = styled.span<{ $size?: ButtonSize }>`
  display: inline-block;
  flex: none;
  width: ${({ $size }) => getSpinnerSize($size)};
  height: ${({ $size }) => getSpinnerSize($size)};
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.6s ${ANIMATION.easing.linear} infinite;
  opacity: 0.9;
`;
